import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@ludus/ui'
import type { CardPage } from '../types.ts'
import { formatDate } from '../utils/format.ts'
import { AudioPlayer, type AudioHandle } from './Audio/AudioPlayer.tsx'
import { FactsPanel } from './Facts/FactsPanel.tsx'
import { HistoryPanel } from './History/HistoryPanel.tsx'
import { IdentityPanel } from './Identity/IdentityPanel.tsx'
import { QaPanel } from './Qa/QaPanel.tsx'
import { StageEvidencePanel } from './StageEvidence/StageEvidencePanel.tsx'
import { TranscriptPanel } from './Transcript/TranscriptPanel.tsx'

interface CardViewProps {
  auditId: string
  page: CardPage
  /** Re-reads the card — and re-mints the signed audio link. */
  refresh: () => Promise<unknown>
}

export interface Interval {
  startMs: number
  endMs: number
}

/**
 * The call card (§10.2, #241): the transcript, its facts and its QA read
 * against each other, the audio seeking to the timecode under the cursor.
 * A deal renders the same frame with its status history and linked calls
 * where the transcript would be. No decision is made here — a doubt is
 * resolved in the queue or the chat.
 */
export function CardView({ auditId, page, refresh }: CardViewProps) {
  const { t } = useTranslation()
  const audioRef = useRef<AudioHandle>(null)
  const [selection, setSelection] = useState<Interval | null>(null)
  const { card } = page

  const seek = (ms: number) => audioRef.current?.seek(ms)
  const select = (interval: Interval | null) => {
    setSelection(interval)
    if (interval) seek(interval.startMs)
  }

  return (
    <div className="flex w-full max-w-6xl flex-1 flex-col gap-6 p-4 sm:p-6">
      <header className="flex flex-col gap-2 border-b border-border/60 pb-4">
        <nav className="text-xs text-muted">
          <Link to={`/audits/${auditId}`} className="hover:underline">
            {t('pages.audits.card.backToAudit', { defaultValue: 'Audit' })}
          </Link>
          {' / '}
          <span>{card.pipelineName}</span>
        </nav>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-semibold text-foreground">{card.name}</h1>
          <Badge tone="muted" size="sm">
            {t(`pages.audits.wizard.kind.${card.kind}`, { defaultValue: card.kind })}
          </Badge>
          {card.statusName && (
            <Badge tone="default" size="sm">
              {card.statusName}
            </Badge>
          )}
          <span className="text-xs text-muted">{formatDate(card.createdAt)}</span>
        </div>
        <ul
          className="flex flex-wrap gap-1"
          aria-label={t('pages.audits.card.stages', { defaultValue: 'Stages' })}
        >
          {page.stages.map((chip) => (
            <li key={chip.stage}>
              <Badge tone={chipTone(chip.state)} size="sm">
                {t(`pages.audits.stages.${chip.stage}`, { defaultValue: chip.stage })} ·{' '}
                {t(`pages.audits.card.unitState.${chip.state}`, { defaultValue: chip.state })}
                {chip.reason
                  ? ` · ${t(`pages.audits.skipReasons.${chip.reason}`, { defaultValue: chip.reason })}`
                  : ''}
              </Badge>
            </li>
          ))}
        </ul>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr]">
        <div className="flex flex-col gap-6">
          {card.kind === 'interaction' ? (
            <>
              <AudioPlayer ref={audioRef} audio={page.audio} onExpired={refresh} />
              <TranscriptPanel transcript={page.transcript} selection={selection} onSeek={seek} />
            </>
          ) : (
            <HistoryPanel
              history={page.history}
              siblings={page.identity.siblings}
              auditId={auditId}
            />
          )}
        </div>
        <div className="flex flex-col gap-6">
          <FactsPanel facts={page.facts} selection={selection} onSelect={select} />
          {card.kind === 'interaction' ? (
            <QaPanel qa={page.qa} />
          ) : (
            <StageEvidencePanel verdicts={page.stageEvidence} />
          )}
          <IdentityPanel auditId={auditId} card={card} identity={page.identity} />
        </div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('pages.audits.card.decisions.title', { defaultValue: 'Decisions' })}
          </CardTitle>
        </CardHeader>
        <CardBody className="text-xs text-muted">
          {t('pages.audits.card.decisions.body', {
            defaultValue:
              'Nothing is resolved from a card. A doubt is decided in the review queue or in the chat.',
          })}{' '}
          <Link className="underline" to={`/audits/${auditId}/review`}>
            {t('pages.audits.card.decisions.queue', { defaultValue: 'Open the queue' })}
          </Link>
        </CardBody>
      </Card>
    </div>
  )
}

function chipTone(state: string): 'success' | 'warning' | 'muted' | 'info' | 'danger' | 'default' {
  switch (state) {
    case 'done':
      return 'success'
    case 'skipped':
      return 'muted'
    case 'queued':
    case 'running':
      return 'info'
    case 'failed':
      return 'danger'
    case 'needs_review':
      return 'warning'
    default:
      return 'default'
  }
}
